import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Building2, Store, School, ClipboardCheck, Map, ArrowRight } from 'lucide-react';

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      number: '01',
      icon: Building2,
      title: 'Vivienda Multifamiliar',
      description: 'Desarrollo integral de torres residenciales y complejos habitacionales de alta densidad.'
    },
    {
      number: '02',
      icon: Store,
      title: 'Inmuebles Comerciales',
      description: 'Ejecución de plazas comerciales, oficinas corporativas y centros de atención especializados.'
    },
    {
      number: '03',
      icon: School,
      title: 'Edificación Institucional',
      description: 'Construcción de hospitales, escuelas y dependencias gubernamentales.'
    },
    {
      number: '04',
      icon: ClipboardCheck,
      title: 'Supervisión de Proyectos',
      description: 'Gestión técnica de obra negra y acabados con alineación arquitectónica perfecta.'
    },
    {
      number: '05',
      icon: Map,
      title: 'Urbanización',
      description: 'Infraestructura vial, hidráulica y eléctrica para grandes complejos urbanos.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section 
      id="servicios" 
      className="py-24 md:py-32 bg-[#F8F9FA]"
      data-testid="services-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-[2px] bg-[#2E75B6]"></div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#2E75B6]">
              Nuestros Servicios
            </p>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#203864] max-w-xl">
              Soluciones Integrales de Construcción
            </h2>
            <p className="text-[#7F7F7F] max-w-md lg:text-right">
              Proyectos de cualquier escala, desde vivienda residencial hasta infraestructura institucional.
            </p>
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200"
        >
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group bg-white p-8 hover:bg-[#203864] transition-all duration-500 cursor-pointer relative overflow-hidden"
                data-testid={`service-card-${index + 1}`}
              >
                {/* Number Background */}
                <span className="absolute -right-4 -top-4 text-[120px] font-black text-[#7F7F7F]/20 group-hover:text-[#2E75B6]/20 transition-colors leading-none select-none">
                  {service.number}
                </span>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-[#F8F9FA] group-hover:bg-[#2E75B6] flex items-center justify-center mb-6 transition-colors">
                    <IconComponent 
                      size={28} 
                      className="text-[#203864] group-hover:text-white transition-colors" 
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-[#203864] group-hover:text-white mb-3 transition-colors">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#7F7F7F] group-hover:text-white/70 transition-colors text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Arrow */}
                  <div className="flex items-center gap-2 text-[#2E75B6] group-hover:text-white transition-colors">
                    <span className="text-sm font-semibold uppercase tracking-wider">Ver más</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            );
          })}
          
          {/* CTA Card */}
          <motion.div
            variants={itemVariants}
            className="bg-[#2E75B6] p-8 flex flex-col justify-center"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              ¿Tienes un proyecto en mente?
            </h3>
            <p className="text-white/80 mb-6">
              Contáctanos para una asesoría personalizada sin compromiso.
            </p>
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 bg-white text-[#203864] px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#203864] hover:text-white transition-colors w-fit"
              data-testid="services-cta"
            >
              Contactar
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
